<template>
  <div class="propertyItem listingItem" :class="{isWithoutBorder, isVertical}">
    <template v-if="property">
      <div v-if="property?.gallery?.length" class="propertyItem__ImagesWrapper">
        <button @click="openGallery" class="propertyItem__MobileGalleryButton">
          <div v-if="property.gallery.length > 1">
            <img src="/resources/images/icons/gallery.svg" width="24" height="24" />
          </div>
          <span class="propertyItem__MobileGalleryButtonOverlay" />
          <Flare :srcset="property.gallery[0].image" size="both" width="644" height="470" crop="fill" :alt="property.gallery[0].image" />
        </button>
        <Modal mobile-only v-model="dialogs.gallery">
          <FlickitySlider v-if="property.gallery" class="propertyItem__Slider" :options="flickityOptions">
            <div v-for="item in property.gallery.slice(0, 5)" :key="item.image.id" class="listingItem__ImageWrapper" :class="{'listingItem__ImageWrapper--Overlay': item.is_overlay}">
              <Flare :srcset="item.image" size="both" width="800" height="600" crop="fill" :alt="item.image" />
              <div class="listingItem__ImageText" v-if="item.is_overlay && item.overlay_text">
                <p>{{ item.overlay_text }}</p>
              </div>
            </div>
          </FlickitySlider>
        </Modal>

        <div v-if="property.brand?.logo" class="propertyItem__GalleryBrand">
          <Flare :srcset="property.brand.logo" width="250" height="100" crop="fill" size="both" :alt="property.brand.logo" />
        </div>
        <!-- <div v-if="property.overlay_image" class="propertyItem__GalleryBadge">
          <Flare :srcset="property.overlay_image" width="250" height="100" crop="fill" size="both" :alt="property.overlay_image" />
        </div> -->
      </div>
      <div v-else-if="property.image">
        <Flare :srcset="property.image" size="both" width="644" height="470" crop="fill" :alt="property.image" />
      </div>
  
      <div class="propertyItem__PropertyData">
        <div>
          <div class="propertyItem__Top">
            <div class="propertyItem__Location">
              <img src="/resources/images/icons/location.svg" width="24" height="24" />
              <template v-if="property.place?.name">{{property.place.name}}</template><template v-if="property.region?.name">, {{property.region.name}}</template>
            </div>
          </div>
          <h2>
            <template v-if="shortName">{{ property.name_short || property.name }}</template>
            <template v-else>{{ property.name }}</template>
          </h2>
          <div v-if="property.category_rating" class="propertyItem__Stars">
            <img v-for="star in property.category_rating" src="/resources/images/icons/star.svg" width="14" height="14" alt="Star" />
          </div>
          <div v-if="property?.usp_verticals" class="propertyItem__Extras">
            <div v-for="item in property.usp_verticals.slice(0, 3)">
              <Flare v-if="item.image" :srcset="item.image" size="both" width="24" height="24" crop="fill" :alt="item.image" />
              {{ item.description }}
            </div>
          </div>
          <div v-if="property.description" class="propertyItem__Description">
            <div v-html="property.description" />
          </div>
        </div>
  
        <div>
          <div v-if="(property.priceForPeriod || property.price_end_of_year) && !withoutPrice" class="propertyItem__AdditionalInfos">
            <div><p>{{ $t('from') }} {{ formatPrice(property.priceForPeriod?.price_from || property.price_end_of_year.price_from, property.priceForPeriod?.currency || property.price_end_of_year.currency, lang, true) }} <template v-if="property.price_from_description">{{ property.price_from_description }}</template></p></div>
          </div>
  
          <div class="propertyItem__ActionButtons" :class="{withoutBookButton}">
            <template v-if="isNotAvailable">
              <Button v-if="property.sef" :to="property.sef" type="nuxt-link" secondary medium>{{ $t('learnMore') }}</Button>
              <Button @click="changeDates(property)" secondary medium>{{ $t('changeDates') }}</Button>
            </template>
            <template v-else>
              <template v-if="withoutBookButton">
                <!-- <nuxt-link :to="property.sef" class="button__Text--Underline">{{ $t('learnMore') }}</nuxt-link> -->
                <button class="button__Text--Underline" @click="goToProperty(property)">{{ $t('learnMore') }}</button>
              </template>
              <template v-else>
                <!-- <Button :to="property.sef" type="nuxt-link" secondary medium>{{ $t('learnMore') }}</Button> -->
                <button class="button--Secondary medium" @click="goToProperty(property)">{{ $t('learnMore') }}</button>
                <Button v-if="accommodationDates" medium @click="goToAccommodation">{{ $t('bookNow') }}</Button>
              </template>
            </template>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
  const { link } = useSetup();
  const { booking } = useBooking();
  const { lang } = useLanguage();
  const { openPanel } = useReservationPanel();
  const { getAvailabilityDates, updateSearchDates } = useAvailabilityDates();
  
  const dialogs = reactive({
    gallery: false
  });
  const { open } = useModal(dialogs);

  const props = defineProps({
    property: {
      type: Object,
    },
    withoutBorder: {
      type: Boolean,
      default: false,
    },
    vertical: {
      type: Boolean,
      default: false,
    },
    withoutBookButton: {
      type: Boolean,
      default: false,
    },
    withoutPrice: {
      type: Boolean,
      default: false,
    },
    notAvailable: {
      type: Boolean,
      default: false,
    },
    shortName: {
      type: Boolean,
      default: false,
    },
    ecommerceItem: {
      type: Object,
      default: {},
    },
  });

  const pageDotsVisible = computed(() => props.property.gallery?.length > 1);
  const isWithoutBorder = computed(() => props.withoutBorder);
  const isVertical = computed(() => props.vertical);
  const isNotAvailable = computed(() => props.notAvailable);
  const flickityOptions = {
    watchCSS: true,
    pageDots: pageDotsVisible.value,
  };
  const accommodationDates = ref(null);

  function openGallery() {
    if (props.property.gallery.length > 1) open('gallery');
  }

  function changeDates(property){
    booking.value.accommodation = property;
    booking.value.accommodation_id = property.id;
    if (booking.value.place_id) delete booking.value.place_id;
    openPanel(1)
  }

  async function goToAccommodation() {
    let item = JSON.parse(JSON.stringify(props.ecommerceItem));
    item.check_in_date = booking?.value?.date_from;
    item.check_out_date = booking?.value?.date_to;
    item.no_adults = booking?.value?.adults_count;
    item.no_children = booking?.value?.children;
    item.coupon = undefined;
    let duration = undefined;
    if(item.check_in_date && item.check_out_date) {
      let diffTime = Math.abs(new Date(item.check_out_date) - new Date(item.check_in_date));
      duration = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      item.no_nights = duration;
    }
    if(booking?.value.promo_code) {
      item.coupon = booking?.value.promo_code;
    }
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      'event': 'check_availability',
      'ecommerce': {
        'items': [item],
      },
    });
    booking.value.accommodation_id = props.property.id;
    delete booking.value.place_id;
    updateSearchDates(props.property?.availability_calendar);
    let fullLink = { path: link('AccommodationUnitList'), query: { accommodation_id: props.property.id } }
    let dates = getAvailabilityDates(props.property?.availability_calendar);
    if (props.property.package_external_id) fullLink.query.package_external_id = props.property.package_external_id;
    if (booking.value.promo_code) {
      fullLink.query.promo_code = booking.value.promo_code;
    }
    fullLink.query.date_from = dates.from;
    fullLink.query.date_to = dates.to;
    fullLink.query.adults_count = booking.value.adults_count;
    fullLink.query.children = booking.value.children;
    await navigateTo(fullLink);
  }

  async function goToProperty(property) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      'event': 'select_item',
      'ecommerce': {
        'items': [props.ecommerceItem],
      },
    });
    await navigateTo(property.sef);
  }

  onMounted(() => {
    if (props.property?.availability_calendar?.length) {
      accommodationDates.value = getAvailabilityDates(props.property?.availability_calendar);
    }
  });

  console.log('props property', props.property);
</script>

<style lang="scss">
  @import url('@/assets/styles/components/_propertyItem.scss');
</style>